import React, { useState, useEffect } from "react";
import "../../styles/ChangePassword.css";
import openNotification from "../utils/CustomNotification";
import axios from "axios";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import PasswordStrengthBar from "react-password-strength-bar";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { handleLogout, useCheckSessionHeartBeat } from "../API/ApiEnpoints";

const ChangePassword: React.FC = () => {
  useCheckSessionHeartBeat();
  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const [showOtpInputs, setShowOtpInputs] = useState<boolean>(false);
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  // const [currentPassword, setCurrentPassword] = useState<string>("defaultCurrentPassword"); // Default current password
  // const [newPassword, setNewPassword] = useState<string>("defaultNewPassword123!"); // Default new password
  // const [confirmPassword, setConfirmPassword] = useState<string>("defaultNewPassword123!");
  const [passwordMismatch, setPasswordMismatch] = useState<boolean>(false);
  const [showCurrentPassword, setShowCurrentPassword] =
    useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [oldPasswordValid, setOldPasswordValid] = useState<boolean>(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState<boolean>(false);
  const [optSession, setOtpSession] = useState<string>("");
  const [otpAttempts, setOtpAttempts] = useState<number>(0);
  const [timer, setTimer] = useState<number>(60); // Timer starts at 60 seconds
  const [isTimerActive, setIsTimerActive] = useState<boolean>(false);
  const [otpSubmissionSuccess, setOtpSubmissionSuccess] =
    useState<boolean>(false);
  const [isOtpSending, setIsOtpSending] = useState(false);
  const [isGetOtpDisabled, setIsGetOtpDisabled] = useState<boolean>(false);

  const user_uid = localStorage.getItem("user_uid") || "";
  const session_uid = localStorage.getItem("session_uid") || "";

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isTimerActive && timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsTimerActive(false);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerActive, timer]);

  useEffect(() => {
    if (
      currentPassword &&
      newPassword &&
      confirmPassword &&
      newPassword === confirmPassword &&
      isPasswordValid(newPassword)
    ) {
      setIsGetOtpDisabled(false);
    }
  }, [currentPassword, newPassword, confirmPassword]);


  const validateOldPassword = async (): Promise<boolean> => {
    try {
      const response = await axios.post(
        `${beAddress}/api/check-user-password`,
        {
          old_password: currentPassword,
          new_password: newPassword,
        },
        {
          headers: {
            "user-uid": user_uid,
          }, withCredentials: true,
        }
      );

      if (response.data.status === "Success") {
        setOldPasswordValid(true);
        setOtpSession(response.data.data.session_uid);
        openNotification({
          type: "success",
          //message: response.data.message,
          message: "OTP has been sent to your email",
        });
        return true;
      } else {
        setOldPasswordValid(false);
        openNotification({
          type: "error",
          message: response.data.data,
        });
        return false;
      }
      //   SessionExtend();
    } catch (error) {
      console.error("Error validating old password:", error);
      openNotification({
        type: "error",
        message: "Server Error",
      });
      return false;
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (/^\d$/.test(value) || value === "") {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value !== "" && index < 5) {
        document.getElementById(`otp-input-${index + 1}`)?.focus();
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && index > 0 && otp[index] === "") {
      document.getElementById(`otp-input-${index - 1}`)?.focus();
    } else if (e.key === "Enter") {
      handleOTPSubmit();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");
    const pasteDigits = pasteData.match(/\d/g);

    if (pasteDigits) {
      const newOtp = pasteDigits
        .slice(0, 6)
        .concat(new Array(6 - pasteDigits.length).fill(""));
      setOtp(newOtp);
      if (pasteDigits.length < 6) {
        document.getElementById(`otp-input-${pasteDigits.length}`)?.focus();
      }
    }
  };

  const handleCurrentPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCurrentPassword(e.target.value);
  };

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewPassword(value);
    if (passwordMismatch && value === confirmPassword) {
      setPasswordMismatch(false);
    }
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (passwordMismatch && value === newPassword) {
      setPasswordMismatch(false);
    }
  };

  const handleOTPSubmit = async () => {
    const changePasswordData = {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
      otp: otp.join(""), // Replace with actual OTP logic
    };
    try {
      const response = await axios.post(
        `${beAddress}/api/change-password`,
        changePasswordData,
        {
          headers: {
            "user-uid": user_uid,
          }, withCredentials: true,
        }
      );

      if (response.status === 200 && response.data.status === "Success") {
        openNotification({
          type: "success",
          message: response.data.data,
        });
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setOtp(new Array(6).fill(""));
        setShowOtpInputs(false);
        setOtpSubmissionSuccess(true);
        await handleLogout();
      } else {
        openNotification({
          type: "error",
          message: response.data.data,
        });
        setOtp(new Array(6).fill(""));
        setOtpAttempts((prev) => prev + 1); // Increment OTP attempts
        if (otpAttempts + 1 >= 3) {
          openNotification({
            type: "error",
            message: "Too many failed attempts. Please request a new OTP.",
          });
          setOtp(new Array(6).fill("")); // Clear OTP input
          setShowOtpInputs(false); // Hide OTP inputs
          setOtpAttempts(0); // Reset attempts
        }
      }
      //SessionExtend();
    } catch (error) {
      console.error("Error updating password:", error);
      openNotification({
        type: "error",
        message: "Server Error",
      });
    }
  };

  const handleGetOtpClick = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      openNotification({
        type: "error",
        message: "Please fill out all password fields before getting the OTP.",
      });
    } else if (newPassword !== confirmPassword) {
      setPasswordMismatch(true);
      openNotification({
        type: "error",
        message: "Passwords do not match.",
      });
    } else if (!isPasswordValid(newPassword)) {
      openNotification({
        type: "error",
        message: "New password does not meet the requirements.",
      });
    } else {
      setIsGetOtpDisabled(true);
      // Validate the old password before showing OTP inputs
      const isOldPasswordValid = await validateOldPassword();
      if (isOldPasswordValid) {
        setShowOtpInputs(true);
        setIsTimerActive(true); // Start the timer
        setTimer(60);
      }
    }
  };

  const handleResetPassword = () => {
    if (!isPasswordValid(newPassword)) {
      openNotification({
        type: "error",
        message: "New password does not meet the requirements.",
      });
      return;
    }

    if (newPassword === confirmPassword) {
      handleOTPSubmit();
    } else {
      setPasswordMismatch(true);
    }
  };

  const isPasswordValid = (password: string): boolean => {
    // Define your password criteria
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return (
      password.length >= minLength &&
      hasUpperCase &&
      hasLowerCase &&
      hasDigit &&
      hasSpecialChar
    );
  };
  const handleClick = async () => {
    setIsOtpSending(true);
    await handleGetOtpClick();
    setIsOtpSending(false);
  };
  return (
    <div className="change-password-main">
      <div className="reset-password-container">
        <h3>Reset password</h3>
        <p className="subtext">Please type something you'll remember</p>
        <p className="subtext">
          For a strong password use symbols, numbers, characters
        </p>

        <form className="reset-password-form">
          <div className="form-group1">
            <label>Old Password</label>
            <div className="password-input-container">
              <input
                type={showCurrentPassword ? "text" : "password"}
                className="new-password-input"
                value={currentPassword}
                onChange={handleCurrentPasswordChange}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              >
                {showCurrentPassword ? (
                  <AiOutlineEyeInvisible />
                ) : (
                  <AiOutlineEye />
                )}
              </button>
            </div>
          </div>
          <div className="form-group1">
            <label>New Password</label>
            <div className="password-input-container">
              <input
                type={showNewPassword ? "text" : "password"}
                className="new-password-input"
                value={newPassword}
                onChange={handleNewPasswordChange}
                data-tooltip-id="new-password-tooltip"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}{" "}
              </button>
              <Tooltip
                id="new-password-tooltip"
                place="bottom"
                className="tooltip-box"
                isOpen={isPasswordFocused}
              >
                <ul>
                  <li>Password must be at least 8 characters long.</li>
                  <li>Password must include at least one lowercase letter.</li>
                  <li>Password must include at least one uppercase letter.</li>
                  <li>Password must include at least one digit.</li>
                  <li>Password must include at least one special character.</li>
                </ul>
              </Tooltip>
            </div>
            {/* {newPassword && <PasswordStrengthBar password={newPassword} />} */}
            {newPassword && (
              <PasswordStrengthBar
                password={newPassword}
                shortScoreWord="Too short"
                scoreWords={
                  isPasswordValid(newPassword)
                    ? ["Weak", "Fair", "Good", "Strong", "Very Strong"]
                    : ["Invalid", "Invalid", "Invalid", "Invalid", "Invalid"]
                }
                barColors={
                  isPasswordValid(newPassword)
                    ? ["#ff4d4d", "#ff944d", "#ffd11a", "#80cc00", "#008000"] // normal colors
                    : ["#ff0000", "#ff0000", "#ff0000", "#ff0000", "#ff0000"] // all red when invalid
                }
              />
            )}

          </div>
          <div className="form-group1">
            <label>Confirm New Password</label>
            <div className="password-input-container">
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="new-password-input"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <AiOutlineEyeInvisible />
                ) : (
                  <AiOutlineEye />
                )}
              </button>
            </div>
            {passwordMismatch && (
              <p className="error-message">Passwords do not match.</p>
            )}
          </div>
          <button
            type="button"
            className="get-otp-button"
            onClick={handleGetOtpClick}
            disabled={isGetOtpDisabled}

          >
            Get OTP
          </button>
        </form>
      </div>

      {showOtpInputs && (
        <div className="otp-container">
          <p>
            We've sent a code to <span className="email">{user_uid}</span>
          </p>
          <p>Enter OTP from Your Email</p>
          <div className="changepassword-reset-otp-inputs">
            {otp.map((digit, index) => (
              <input
                key={index}
                type="text"
                maxLength={1}
                className="otp-input"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={handlePaste}
                id={`otp-input-${index}`}
              />
            ))}
          </div>
          <button
            type="button"
            className="reset-password-button"
            onClick={handleResetPassword}
          >
            Reset Password
          </button>
          <div className="otp-timer">
            {isTimerActive ? (
              <p>Resend OTP in {timer} seconds</p>
            ) : (
              <p>
                <span
                  className="resend-otp-link"
                  onClick={timer === 0 && !isOtpSending ? handleClick : undefined}
                  style={{
                    cursor:
                      timer === 0 && !isOtpSending ? "pointer" : "not-allowed",
                    color: timer === 0 && !isOtpSending ? "blue" : "gray",
                    pointerEvents:
                      timer === 0 && !isOtpSending ? "auto" : "none",
                  }}
                >
                  Resend OTP
                </span>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangePassword;
